import { motion } from "framer-motion";
import { MessageSquare, Mic, MicOff, Phone, X } from "lucide-react";
import HeaderSection from "./header-section";
import { VoiceWave } from "./VoiceWave";
import ChatInterface2 from "./chat-interface-2";
import { ChatWidgetProps } from '../types'
import { useAppDataStore } from "../../contexts/data.context";
import { useEffect, useState } from "react";
import { API_URL } from "../../lib/_base";
import { mergeClasses } from "../../lib/mergeClasses";

const ChatWidgetModern = ({
  setShowCall,
  setShowChat,
  onCallNow,
  onChatNow,
  connected,
  chatHistory,
  isThinking,
  isPTTActive,
  showChat,
  handleDisconnect,
  handleTextInput,
  handlePTTStart,
  handlePTTEnd,
  isPlaying,
  isMinimize,
  setIsMinimize
}: ChatWidgetProps) => {
  const [currentEvent, setCurrentEvent] = useState<string | null>(null);
  const [actionPending, setActionPending] = useState<boolean>(false);

  const { visualEvents } = useAppDataStore()

  useEffect(() => {
    visualEvents.on('navigation:start', () => {
      setCurrentEvent('navigating')
    })

    visualEvents.on('navigation:end', () => {
      let tx = setTimeout(() => {
        setCurrentEvent(null)
        clearTimeout(tx)
      }, 1000)
    })

    visualEvents.on('user_action:buy_now', () => {
      setCurrentEvent('buy_now')
    })

    visualEvents.on('user_action:clear', () => {
      setCurrentEvent(null)
    })

    return () => {
      visualEvents.off('navigation:start')
      visualEvents.off('navigation:end')
      visualEvents.off('user_action:buy_now')
      visualEvents.off('user_action:clear')
    }
  }, [])

  const handleClose = () => {
    setShowCall(false);
    setShowChat(false);
    handleDisconnect();
  }

  function handleBuyNow() {
    try {
      const url = new URL('/data/mock_checkout', API_URL)
      setActionPending(true)
      fetch(url)
        .then(async (resp) => {
          if (resp.ok) {
            const body = await resp.json()
            const checkout_url = body.checkout_url
            window.open(checkout_url, '_blank')
          } else {
            console.error(await resp.text())
          }
        })
        .finally(() => setActionPending(false))
    } catch (err) {
      console.trace(err)
    }
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.5, x: 100, y: 100 }}
      animate={{
        opacity: 1,
        scale: 1,
        x: 0,
        y: 0,
        height: isMinimize ? 80 : showChat ? 600 : 340,
      }}
      transition={{ type: "spring", stiffness: 100, damping: 15 }}
      className={`!fixed !bottom-6 !right-8 !w-80 !rounded-3xl !shadow-lg !overflow-hidden !bg-[var(--sonaris-color-secondary)]  ${isMinimize ? "!h-fit" : ""
        }`}
    >
      <div className="!flex !flex-col !gap-3 !justify-between !text-white px-6 py-4  h-full">
        <HeaderSection
          handleClose={handleClose}
          isPlaying={isPlaying}
          isThinking={isThinking}
          isMinimize={isMinimize}
          setIsMinimize={setIsMinimize}
          hideAudioAnimation={showChat && currentEvent !== null}
        />

        {
          currentEvent === 'navigating' && (
            <div className="!px-4 !py-2 !rounded-full !border !border-[var(--sonaris-color-primary)]/50 !w-full !text-center !text-sm !bg-[var(--sonaris-color-primary)]/10 animate-pulse">
              Navigating
            </div>
          )
        }

        {
          currentEvent === 'buy_now' && (
            <button className={mergeClasses("!px-4 !py-2 !rounded-full !border !border-white !w-full !text-center !text-sm !bg-white !text-[var(--sonaris-color-secondary)] hover:!bg-gray-200 transition-all", {
              'animate-pulse': actionPending
            })} onClick={handleBuyNow} disabled={actionPending}>
              Buy Now
            </button>
          )
        }

        {
          showChat && connected && !isMinimize && (
            <ChatInterface2
              key="chat-interface"
              handleSubmitTextInput={handleTextInput}
              chatHistory={chatHistory}
              isThinking={isThinking}
            />
          )
        }

        {!isMinimize && <div className="!flex !flex-col !gap-2">
          <div className="!border-t-2 !border-white/10 ">
            <div className="!h-16 !flex !items-center !justify-between !w-full !pt-4">
              {!showChat ?
                (
                  <div
                    className="!flex !items-center !justify-center !p-3 !bg-[var(--sonaris-color-primary)]/30 !rounded-full !cursor-pointer"
                    onClick={() => {
                      // setShowChat(true);
                      // onChatNow()
                      setShowChat(true)
                      // setShowCall(true)
                      // updateConfig({ activeMode: 'chat' })
                      // setActiveMode('chat')
                      // Don't call onChatNow() again if already connected
                      if (!connected) {
                        onChatNow()
                      }
                    }}>
                    <MessageSquare size={20} className="opacity-70" />
                  </div>
                ) : (
                  <div
                    className="!flex !items-center !justify-center !p-3 !bg-[var(--sonaris-color-primary)]/30 !rounded-full !cursor-pointer"
                    onClick={() => {
                      // setShowChat(false)
                      // onCallNow()
                      setShowChat(false)
                      // updateConfig({ activeMode: 'call' })
                      // setActiveMode('call')
                      // Don't call onCallNow() again if already connected
                      if (!connected) {
                        onCallNow()
                      }
                    }}>
                    <X size={20} className="!opacity-70" />
                  </div>
                )}
              <div className="!flex !items-center !justify-center">
                <VoiceWave isWaveMoving={isPTTActive} />
              </div>
              <div className="!flex !items-center !gap-2">
                <div className="!flex !items-center !justify-center !p-3 !bg-[var(--sonaris-color-primary)]/30 !rounded-full !cursor-pointer"
                  // onTouchStart={handlePTTStart}
                  // onMouseDown={handlePTTStart}
                  // onTouchEnd={handlePTTEnd}
                  // onMouseUp={handlePTTEnd}
                  onPointerDown={handlePTTStart}
                  onPointerUp={handlePTTEnd}
                >
                  {!isPTTActive ?
                    <MicOff size={20} className="!opacity-70" />
                    :
                    <Mic size={20} className="!opacity-70" />
                  }
                </div>
                <div
                  className="!flex !items-center !justify-center !p-3 !bg-[#e5464a] !rounded-full !cursor-pointer"
                  onClick={handleClose}>
                  {/* end call */}
                  <Phone size={20} className="!opacity-70" />
                </div>
              </div>
            </div>
          </div>

          <div className="!flex !justify-center !items-center !text-xs !text-white/50 ">
            <span>Powered by</span>
            <span className="!ml-1 !font-medium !flex !items-center">
              <svg viewBox="0 0 24 24" fill="none" className="w-3 h-3 mr-1">
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M8 12h8M12 8v8"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              <a href="https://sonaris.ai" className="!underline !text-inherit" target="_blank">Sonaris.ai</a>
            </span>
          </div>
        </div>}
      </div>
    </motion.div >
  );
};

export default ChatWidgetModern
